/**
 * Permission Table (ORCH-415, ORCH-21 §3.7, §3.12, §11)
 *
 * Purpose: The one grouped table of everything a role grants, organised by
 *          Area. An Area row carries the Hidden / Read only / Editable
 *          selector; under it sit its permissions, nested where the catalog's
 *          `requires` makes a hierarchy (Media plans ▸ Flights ▸ Line items)
 *          and its sub-areas (Maestro under Planning, the managers under
 *          Admin). Scoped rows keep a ScopeCell per dimension; unscoped rows
 *          show one merged On / – cell. Groups start collapsed; a group holding
 *          a row that blocks the save, the row a refused save named, or a row
 *          just ticked for the user opens by itself. The header and the
 *          Permission column stay put while the box scrolls.
 * Rules: what a click derives (parents, dependencies, included keys, the
 *        plan → flight → line item chain, Inherit as the starting scope) lives
 *        in roleDraft.ts and matches RoleService.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ChevronRight as CollapsedIcon,
  ExpandMore as ExpandedIcon,
  UnfoldLess as DetailCloseIcon,
  UnfoldMore as DetailOpenIcon,
} from '@mui/icons-material';

import { Narrowing } from '../../../hooks/useNarrowedPickerValues';
import { FieldDefinition } from '../../../services/metadataService';
import {
  Dimension,
  ModuleDef,
  ModuleLevel,
  PermissionCatalog,
  PermissionDef,
} from '../../../types/authz';
import DescriptionInfo from './DescriptionInfo';
import ScopeCell from './ScopeCell';
import {
  DISPLAY_LEVELS,
  Level,
  RoleDraft,
  applyModuleLevel,
  applyTogglePermission,
  derivedLevel,
  featureDisabled,
  impliedBy,
  isDerived,
  levelAvailability,
  setCell,
  setColumnAll,
  setRow,
} from './roleDraft';

const LEVEL_LABELS: Record<ModuleLevel, string> = { read: 'Read only', edit: 'Editable' };
const OTHER = 'Other';

/** What a narrowed dimension's picker follows on this row: the other dimension's picked values. */
export const narrowingFor = (
  draft: RoleDraft,
  permissionKey: string,
  dimension: Dimension
): Narrowing | null => {
  const by = dimension.narrowed_by;
  if (!by) return null;
  const other = draft.permissions[permissionKey]?.[by.dimension_key];
  return {
    fielddefId: dimension.picker_fielddef_id,
    byFielddefId: by.picker_fielddef_id,
    relatedFielddefId: by.fielddef_id,
    chosen: other?.mode === 'override' ? other.value_ids : [],
  };
};

/** A sub-group of an Area's permissions, nested by `requires` (Flights under Media plans). */
export interface GroupNode {
  id: string;
  label: string;
  permissions: PermissionDef[];
  children: GroupNode[];
}

/** An Area: its module, its own permission groups, and the areas under it. */
export interface AreaNode {
  id: string;
  module: ModuleDef | null;
  label: string;
  groups: GroupNode[];
  children: AreaNode[];
}

/**
 * Build the Area tree the table renders from the catalog alone
 *
 * Logic: top-level modules are Areas, with their sub-modules nested. Each
 *        permission sits under its `area`, grouped by `group`; a group whose
 *        keys require a key in another group nests under that one. Keys with
 *        no area go under "Other".
 */
export const buildAreaTree = (catalog: PermissionCatalog): AreaNode[] => {
  const byKey = Object.fromEntries(catalog.permissions.map((p) => [p.key, p]));
  const rows = catalog.permissions.filter((p) => p.resource !== 'module');
  const groupsFor = (areaKey: string | null): GroupNode[] => {
    const own = rows.filter((p) => (p.area ?? null) === areaKey);
    const byLabel = new Map<string, PermissionDef[]>();
    own.forEach((p) => {
      const label = p.group ?? OTHER;
      byLabel.set(label, [...(byLabel.get(label) ?? []), p]);
    });
    const nodes = new Map<string, GroupNode>(
      [...byLabel].map(([label, permissions]) => [
        label,
        { id: `group:${areaKey ?? OTHER}:${label}`, label, permissions, children: [] },
      ])
    );
    const roots: GroupNode[] = [];
    nodes.forEach((node, label) => {
      const parentLabel = node.permissions
        .map((p) => (p.requires ? byKey[p.requires] : undefined))
        .map((parent) => (parent && (parent.area ?? null) === areaKey ? parent.group : null))
        .find((l) => l && l !== label && nodes.has(l));
      if (parentLabel) (nodes.get(parentLabel) as GroupNode).children.push(node);
      else roots.push(node);
    });
    return roots;
  };
  const areaFor = (module: ModuleDef): AreaNode => ({
    id: module.key,
    module,
    label: module.label,
    groups: groupsFor(module.key),
    children: catalog.modules.filter((m) => m.parent === module.key).map(areaFor),
  });
  const tree = catalog.modules.filter((m) => !m.parent).map(areaFor);
  const other = groupsFor(null);
  if (other.length)
    tree.push({ id: OTHER, module: null, label: OTHER, groups: other, children: [] });
  return tree;
};

const groupKeys = (g: GroupNode): string[] => [
  ...g.permissions.map((p) => p.key),
  ...g.children.flatMap(groupKeys),
];
const areaKeys = (a: AreaNode): string[] => a.groups.flatMap(groupKeys);

/** The node ids on the way to each key, so a key's ancestors can be opened. */
const ancestorsOf = (tree: AreaNode[]): Record<string, string[]> => {
  const out: Record<string, string[]> = {};
  const visitGroup = (g: GroupNode, path: string[]) => {
    g.permissions.forEach((p) => {
      out[p.key] = [...path, g.id];
    });
    g.children.forEach((c) => visitGroup(c, [...path, g.id]));
  };
  const visitArea = (a: AreaNode, path: string[]) => {
    out[a.id] = path;
    a.groups.forEach((g) => visitGroup(g, [...path, a.id]));
    a.children.forEach((c) => visitArea(c, [...path, a.id]));
  };
  tree.forEach((a) => visitArea(a, []));
  return out;
};

const allNodeIds = (tree: AreaNode[]): string[] => {
  const ids: string[] = [];
  const visitGroup = (g: GroupNode) => {
    ids.push(g.id);
    g.children.forEach(visitGroup);
  };
  const visitArea = (a: AreaNode) => {
    ids.push(a.id);
    a.groups.forEach(visitGroup);
    a.children.forEach(visitArea);
  };
  tree.forEach(visitArea);
  return ids;
};

// The Permission column stays in view while the dimension columns scroll sideways
const stickyColumn = {
  position: 'sticky',
  left: 0,
  zIndex: 1,
  bgcolor: 'background.paper',
} as const;

const rank = (level: Level) => (level === 'edit' ? 2 : level === 'read' ? 1 : 0);

interface PermissionTableProps {
  catalog: PermissionCatalog;
  dimensions: Dimension[];
  /** The tenant whose values a narrowed picker reads. */
  tenantId: string;
  fieldDefinitions: Record<string, FieldDefinition>;
  /** Dimensions whose picker field couldn't be loaded. */
  failedPickers: Set<string>;
  draft: RoleDraft;
  fromModule: Set<string>;
  needed: Set<string>;
  /** The key a refused save named, highlighted and opened. */
  errorKey?: string | null;
  readOnly?: boolean;
  /** Is the role GLOBAL's? Its levels are judged in its own tenant, not the viewer's. */
  roleInGlobal: boolean;
  /** Does the editor hold this permission? Others can stay or go, not change (decision #62). */
  holds: (key: string) => boolean;
  /** Does the editor hold this module at this level? Only those can be raised to. */
  holdsLevel: (key: string, level: ModuleLevel) => boolean;
  /** The active tenant's enabled features: an area needing another is greyed out. */
  featureToggles: string[];
  onChange: (draft: RoleDraft) => void;
  /** Something the table did for the user, to show above it. */
  onNotice?: (notice: string) => void;
  /**
   * An expandable sub-row under a permission: the slot for finer choices such as
   * which field definitions "View master data" covers. Nothing fills it yet.
   */
  detail?: (permission: PermissionDef) => React.ReactNode;
}

const PermissionTable: React.FC<PermissionTableProps> = ({
  catalog,
  dimensions,
  tenantId,
  fieldDefinitions,
  failedPickers,
  draft,
  fromModule,
  needed,
  errorKey,
  readOnly,
  roleInGlobal,
  holds,
  holdsLevel,
  featureToggles,
  onChange,
  onNotice,
  detail,
}) => {
  const tree = useMemo(() => buildAreaTree(catalog), [catalog]);
  const ancestors = useMemo(() => ancestorsOf(tree), [tree]);
  const labels = useMemo(
    () =>
      Object.fromEntries([...catalog.permissions, ...catalog.modules].map((d) => [d.key, d.label])),
    [catalog]
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [openDetails, setOpenDetails] = useState<Set<string>>(new Set());
  const reasons = impliedBy(draft, catalog);
  const unlicensed = featureDisabled(catalog, featureToggles);

  const open = (ids: string[]) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });

  // A row that blocks the save, or that a refused save named, must be seen
  useEffect(() => {
    const keys = [...needed, ...(errorKey ? [errorKey] : [])];
    const ids = keys.flatMap((k) => ancestors[k] ?? []);
    if (ids.length) open(ids);
  }, [needed, errorKey, ancestors]);

  const toggleNode = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleLevel = (module: ModuleDef, level: Level) => {
    const change = applyModuleLevel(draft, catalog, dimensions, module.key, level);
    onChange(change.draft);
    if (change.added.length) {
      open(change.added.flatMap((k) => ancestors[k] ?? []));
      onNotice?.(
        `${module.label} added ${change.added.map((k) => labels[k] ?? k).join(', ')} at Inherit on every dimension. Open the area to narrow or widen them.`
      );
    }
  };

  const handleToggle = (permission: PermissionDef, on: boolean) => {
    const change = applyTogglePermission(draft, catalog, dimensions, permission.key, on);
    onChange(change.draft);
    if (change.autoTicked.length) {
      open(change.autoTicked.flatMap((k) => ancestors[k] ?? []));
      onNotice?.(
        `${permission.label} needs ${change.autoTicked.map((k) => labels[k] ?? k).join(' and ')}, so they were granted too.`
      );
    }
    if (change.cascaded.length) {
      open(change.cascaded.flatMap((k) => ancestors[k] ?? []));
      onNotice?.(
        `Removing ${permission.label} also removed ${change.cascaded.map((k) => labels[k] ?? k).join(', ')}, which need it.`
      );
    }
  };

  const summarise = (keys: string[]) => {
    const granted = keys.filter((k) => k in draft.permissions).length;
    const missing = keys.filter((k) => needed.has(k)).length;
    return `${granted} of ${keys.length} granted${missing ? ` · ${missing} needs scope` : ''}`;
  };

  const chevron = (id: string, label: string) => (
    <IconButton
      size="small"
      onClick={() => toggleNode(id)}
      aria-label={`${expanded.has(id) ? 'Collapse' : 'Expand'} ${label}`}
      aria-expanded={expanded.has(id)}
      data-testid={`area-toggle-${id}`}
      sx={{ p: 0.25 }}
    >
      {expanded.has(id) ? <ExpandedIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
    </IconButton>
  );

  const renderPermission = (p: PermissionDef, depth: number): React.ReactNode => {
    const granted = p.key in draft.permissions;
    const locked = fromModule.has(p.key);
    const held = holds(p.key);
    const unavailable = p.global_only && !roleInGlobal;
    const frozen = readOnly || !held;
    const showDetail = detail && openDetails.has(p.key);
    return (
      <React.Fragment key={p.key}>
        <TableRow data-testid={`matrix-row-${p.key}`} selected={errorKey === p.key}>
          <TableCell sx={{ ...stickyColumn, pl: 1 + depth * 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Checkbox
                checked={granted}
                disabled={readOnly || locked || unavailable || (!held && !granted)}
                onChange={(e) => handleToggle(p, e.target.checked)}
                inputProps={{ 'aria-label': `Grant ${p.label}` }}
                data-testid={`grant-${p.key}`}
                sx={{ p: 0.5 }}
              />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2">{p.label}</Typography>
                  <DescriptionInfo itemKey={p.key} label={p.label} description={p.description} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {p.key}
                </Typography>
              </Box>
              {locked && <Chip size="small" label="from area" variant="outlined" />}
              {unavailable && <Chip size="small" label="GLOBAL only" variant="outlined" />}
              {!held && !readOnly && (
                <Tooltip title="You don't hold this permission, so you can remove it but not grant or rescope it">
                  <Chip size="small" label="not yours to change" variant="outlined" />
                </Tooltip>
              )}
              {needed.has(p.key) && (
                <Chip
                  size="small"
                  color="warning"
                  label="scope needed"
                  data-testid={`scope-needed-${p.key}`}
                />
              )}
              {p.scoped && granted && !frozen && (
                <Typography variant="caption" component="span">
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => onChange(setRow(draft, p.key, dimensions, 'all'))}
                    aria-label={`ALL on every dimension for ${p.label}`}
                    data-testid={`row-all-${p.key}`}
                  >
                    All
                  </Link>
                  {' · '}
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => onChange(setRow(draft, p.key, dimensions, 'inherit'))}
                    aria-label={`Inherit on every dimension for ${p.label}`}
                    data-testid={`row-inherit-${p.key}`}
                  >
                    Inherit
                  </Link>
                </Typography>
              )}
              {detail && (
                <IconButton
                  size="small"
                  aria-label={`${showDetail ? 'Hide' : 'Show'} details for ${p.label}`}
                  data-testid={`detail-toggle-${p.key}`}
                  onClick={() =>
                    setOpenDetails((prev) => {
                      const next = new Set(prev);
                      if (next.has(p.key)) next.delete(p.key);
                      else next.add(p.key);
                      return next;
                    })
                  }
                  sx={{ p: 0.25 }}
                >
                  {showDetail ? (
                    <DetailCloseIcon fontSize="small" />
                  ) : (
                    <DetailOpenIcon fontSize="small" />
                  )}
                </IconButton>
              )}
            </Box>
          </TableCell>
          {p.scoped ? (
            dimensions.map((d) => (
              <TableCell key={d.key} sx={{ verticalAlign: 'top' }}>
                {granted ? (
                  <ScopeCell
                    permissionKey={p.key}
                    permissionLabel={p.label}
                    dimensionKey={d.key}
                    dimensionLabel={d.label}
                    scope={draft.permissions[p.key][d.key]}
                    fieldDefinition={fieldDefinitions[d.key]}
                    readOnly={frozen}
                    loadFailed={!fieldDefinitions[d.key] && failedPickers.has(d.key)}
                    narrowing={narrowingFor(draft, p.key, d)}
                    tenantId={tenantId}
                    onChange={(scope) => onChange(setCell(draft, p.key, d.key, scope))}
                  />
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    –
                  </Typography>
                )}
              </TableCell>
            ))
          ) : (
            <TableCell colSpan={Math.max(dimensions.length, 1)} data-testid={`on-${p.key}`}>
              <Typography variant="body2" color={granted ? 'text.primary' : 'text.disabled'}>
                {granted ? 'On' : '–'}
              </Typography>
            </TableCell>
          )}
        </TableRow>
        {showDetail && (
          <TableRow data-testid={`detail-${p.key}`}>
            <TableCell colSpan={dimensions.length + 1} sx={{ pl: 4 + depth * 2 }}>
              <Collapse in>{detail(p)}</Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  };

  const renderGroup = (g: GroupNode, depth: number, nested: boolean): React.ReactNode => {
    const isOpen = !nested || expanded.has(g.id);
    return (
      <React.Fragment key={g.id}>
        <TableRow data-testid={`group-${g.id}`}>
          <TableCell sx={{ ...stickyColumn, pl: 1 + depth * 2, py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {nested && chevron(g.id, g.label)}
              <Typography variant="overline">{g.label}</Typography>
            </Box>
          </TableCell>
          <TableCell colSpan={Math.max(dimensions.length, 1)} sx={{ py: 0.5 }}>
            {nested && !isOpen && (
              <Typography variant="caption" color="text.secondary">
                {summarise(groupKeys(g))}
              </Typography>
            )}
          </TableCell>
        </TableRow>
        {isOpen && g.permissions.map((p) => renderPermission(p, depth + 1))}
        {isOpen && g.children.map((c) => renderGroup(c, depth + 1, true))}
      </React.Fragment>
    );
  };

  const renderSelector = (module: ModuleDef) => {
    const level = draft.modules[module.key] ?? null;
    const disabled = Boolean(readOnly) || unlicensed.has(module.key);
    const availability = readOnly
      ? { read: true, edit: true }
      : levelAvailability(module, catalog, roleInGlobal);
    if (!availability.read && !availability.edit) {
      return (
        <Typography variant="caption" color="text.secondary">
          Only in GLOBAL
        </Typography>
      );
    }
    const cantRaise = (option: ModuleLevel) =>
      rank(option) > rank(level) && !holdsLevel(module.key, option);
    return (
      <ToggleButtonGroup
        size="small"
        exclusive
        value={level ?? 'hidden'}
        disabled={disabled}
        onChange={(_, next: string | null) => {
          if (next === null) return;
          handleLevel(module, next === 'hidden' ? null : (next as Level));
        }}
        aria-label={`${module.label} level`}
      >
        <ToggleButton
          value="hidden"
          disabled={disabled}
          data-testid={`level-${module.key}-hidden`}
          sx={{ py: 0.25 }}
        >
          Hidden
        </ToggleButton>
        {DISPLAY_LEVELS.map((option) => {
          const unavailable = !availability[option];
          const title = unavailable
            ? module.levels.includes(option)
              ? 'Only in GLOBAL'
              : 'Not available yet'
            : cantRaise(option)
              ? "You can't grant a level you don't hold"
              : undefined;
          return (
            <Tooltip key={option} title={title ?? ''} disableHoverListener={!title}>
              <span>
                <ToggleButton
                  value={option}
                  // The group's own `disabled` doesn't reach a button that sets its own
                  disabled={disabled || unavailable || cantRaise(option)}
                  data-testid={`level-${module.key}-${option}`}
                  sx={{ py: 0.25 }}
                >
                  {LEVEL_LABELS[option]}
                </ToggleButton>
              </span>
            </Tooltip>
          );
        })}
      </ToggleButtonGroup>
    );
  };

  const renderArea = (a: AreaNode, depth: number): React.ReactNode => {
    const isOpen = expanded.has(a.id);
    const module = a.module;
    const derived = isDerived(module ?? undefined);
    const level = module
      ? derived
        ? derivedLevel(draft, catalog, module.key)
        : (draft.modules[module.key] ?? null)
      : null;
    const keys = [...areaKeys(a), ...a.children.flatMap(areaKeys)];
    return (
      <React.Fragment key={a.id}>
        <TableRow
          data-testid={`module-${a.id}`}
          selected={Boolean(module) && errorKey === module?.key}
          sx={{ bgcolor: depth === 0 ? 'action.hover' : undefined }}
        >
          <TableCell sx={{ ...stickyColumn, pl: 1 + depth * 2, bgcolor: 'inherit' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {chevron(a.id, a.label)}
              <Typography variant="body2" fontWeight={depth === 0 ? 600 : 500}>
                {a.label}
              </Typography>
              {module && (
                <DescriptionInfo
                  itemKey={module.key}
                  label={module.label}
                  description={module.description}
                />
              )}
            </Box>
            {module && errorKey === module.key && (
              <Typography variant="caption" color="error.main" display="block">
                The last save was refused here
              </Typography>
            )}
            {module && unlicensed.has(module.key) && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                data-testid={`module-${module.key}-unlicensed`}
              >
                Not enabled for this tenant
              </Typography>
            )}
            {module && reasons[module.key] && (
              <Typography variant="caption" color="text.secondary" display="block">
                Needed by {reasons[module.key].join(', ')}
              </Typography>
            )}
          </TableCell>
          <TableCell colSpan={Math.max(dimensions.length, 1)} sx={{ bgcolor: 'inherit' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {module &&
                (derived ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={`derived-${module.key}`}
                  >
                    {level ? 'Read only' : 'Hidden'} · follows the managers below
                  </Typography>
                ) : (
                  renderSelector(module)
                ))}
              {!isOpen && keys.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  data-testid={`summary-${a.id}`}
                >
                  {module && !derived ? `${level ? LEVEL_LABELS[level] : 'Hidden'} · ` : ''}
                  {summarise(keys)}
                </Typography>
              )}
            </Box>
          </TableCell>
        </TableRow>
        {isOpen && a.groups.map((g) => renderGroup(g, depth + 1, false))}
        {isOpen && a.children.map((c) => renderArea(c, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" sx={{ alignSelf: 'flex-end' }}>
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setExpanded(new Set(allNodeIds(tree)))}
          data-testid="permissions-expand-all"
        >
          Expand all
        </Link>
        {' · '}
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setExpanded(new Set())}
          data-testid="permissions-collapse-all"
        >
          Collapse all
        </Link>
      </Typography>
      <TableContainer
        sx={{ maxHeight: 'calc(100vh - 240px)', overflow: 'auto' }}
        data-testid="permission-matrix-container"
      >
        <Table size="small" stickyHeader aria-label="Permissions">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...stickyColumn, zIndex: 3, minWidth: 280 }}>Permission</TableCell>
              {dimensions.map((d) => (
                <TableCell key={d.key}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">{d.label}</Typography>
                    {!readOnly && (
                      <Tooltip title={`ALL ${d.label} for every granted permission`}>
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={() => onChange(setColumnAll(draft, catalog, d.key, holds))}
                          data-testid={`column-all-${d.key}`}
                        >
                          All
                        </Link>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>{tree.map((a) => renderArea(a, 0))}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PermissionTable;
